
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { WelcomeHeader } from "./components/WelcomeHeader";
import { QuickStats } from "./components/QuickStats";
import { HeroBanner } from "./components/HeroBanner";
import { NotificationsCard } from "./components/NotificationsCard";
import { FeatureCards } from "./components/FeatureCards";
import { AdaptiveContainer } from "@/components/ui/adaptive-container";
import { AdaptiveGrid } from "@/components/ui/adaptive-grid";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getFeaturesByRole } from "./features/dashboardFeatures";
import { UserProfile, DatabaseUserProfile, RoleType } from "@/types/profile";

export const DashboardHome = () => {
  const { user } = useAuth();
  const [userRole, setUserRole] = useState<string>("Patron");

  // Fetch user profile data
  const { data: dbProfile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();
        
      if (error) {
        console.error("Error fetching profile:", error);
        return null;
      }
      
      return data as DatabaseUserProfile;
    },
    enabled: !!user?.id,
  });

  // Convert database profile to typed profile
  const profile: UserProfile | null = dbProfile ? {
    ...dbProfile,
    notification_preferences: (dbProfile.notification_preferences as any) || {
      email: false,
      push: false,
      sms: false
    },
    social_links: (dbProfile.social_links as any) || {},
    streaming_links: (dbProfile.streaming_links as any) || {},
    testimonials: (dbProfile.testimonials as any) || [],
    role_types: Array.isArray(dbProfile.role_types) 
      ? (dbProfile.role_types as any[]).filter((role: any): role is RoleType => 
          ['DJ', 'Performer', 'Employee', 'Security', 'Promoter', 'Venue Owner', 'Patron'].includes(role)
        )
      : ['Patron'],
    privacy_settings: (dbProfile.privacy_settings as any) || {
      public_profile: false,
      show_contact: false,
      show_rates: false,
      enable_booking: false
    },
    rate_card: (dbProfile.rate_card as any) || {},
    availability: (dbProfile.availability as any) || {},
    payment_details: (dbProfile.payment_details as any) || {
      preferred_method: 'paypal'
    }
  } : null;

  useEffect(() => {
    if (profile?.role_types && profile.role_types.length > 0) {
      setUserRole(profile.role_types[0]);
    } else if (user?.user_metadata?.role) {
      setUserRole(user.user_metadata.role as string);
    }
  }, [user, profile]);

  // Get dashboard features for the current role
  const dashboardFeatures = getFeaturesByRole(profile?.role_types || [userRole as RoleType]);

  return (
    <AdaptiveContainer spacing="normal" className="min-h-screen relative">
      {/* Background Effects */}
      <div className="fixed inset-0 bg-gradient-to-br from-background via-background to-background/80" />
      <div className="fixed inset-0 bg-gradient-to-tr from-primary/5 via-transparent to-secondary/5" />
      
      <div className="relative z-10 space-y-6 lg:space-y-8">
        <WelcomeHeader profile={profile} />
        <QuickStats profile={profile} />
        <HeroBanner />
        
        <AdaptiveGrid 
          adaptiveColumns={{ mobile: 1, tablet: 1, laptop: 3, desktop: 3 }}
          gap="lg"
        >
          <div className="lg:col-span-2">
            <FeatureCards features={dashboardFeatures} />
          </div>
          <div className="space-y-6">
            <NotificationsCard />
          </div>
        </AdaptiveGrid>
      </div>
    </AdaptiveContainer>
  );
};
